import React, { useCallback, useEffect, useState } from 'react';
import { Card, Grid, Box, Switch, FormControlLabel, Typography, Tooltip, Backdrop, IconButton, Snackbar, Alert, TableContainer, Table, TableHead, TableCell, Stack, TableRow, TableBody, Icon, Checkbox, Dialog, DialogTitle } from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import { useDispatch, useSelector } from 'react-redux';
import { PmConfigType, defaultConfigValues } from './pmConfigType';
import SwitchConfig from './components/SwitchConfig';
import BeforeAfterSwitchConfig from './components/BeforeAfterSwitchConfig';
import InputConfig from './components/InputConfig';
import ReadOnlyConfig from './components/ReadOnlyConfig';
import FrequencyConfig from './components/FrequencyConfig';
import ConfirmRestoreDefaultsDialog from './components/confirmRestoreDefaultsDialog';
import { useInosioUser } from '../../shared/login/useInosioUser';
import { useHistory, useParams } from 'react-router';
import whiteBackIcon from '../../icons/whiteBackIcon.svg';
import DateConfig from './components/DateConfig';
import { convertCentsToDollars } from '../../shared/util/converters';
import AmountInputConfig from './components/AmountInputConfig';
import { useStyles } from './styles';
import NumberFormat from 'react-number-format';
import AddIcon from '../../icons/addcard.png';
import EditIcon from '../../icons/editIcon.svg';
import DeleteIcon from '../../icons/deleteIcon.svg';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteModal from './components/DeleteModal';
import { QuickPayTypeDto } from '@inosio/payments-sdk';
import SpinnerModal from '../../shared/Modal/SpinnerModal';
import QuickPayModal, { QuickPayModalType } from './components/CreateEditQuickPayModal';
import { getAvailableBankAccount } from '../billingConfiguration/sagas/billingSlice';
import debounce from 'lodash.debounce';
import { clearPropertyGroupConfigSettingsFlags, getAllPropertyGroupsConfigs, getUpdatePropertyGroupConfigs, selectPropertyGroupDetailsState, selectSpecificPropertyGroupConfigs } from '../../shared/sagas/propertyGroups/propertyGroupsSlice';

interface ErrorObj {
  [key: string]: string;
}

export default function PaymentsConfigSettings() {
  const { uuid: propertyGroupIdParam } = useParams<{ uuid: string }>();
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = useStyles();
  const [isInosioUser] = useInosioUser();
  const [snackBarOpen, setSnackBarOpen] = React.useState(false);
  const [copySnackBarOpen, setCopySnackBarOpen] = React.useState(false);
  const [pmConfig, setPmConfig] = useState<PmConfigType>({}); //holds the working copy while editing

  const handleAlertClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setSnackBarOpen(false);
    setCopySnackBarOpen(false);
  };

  const { selectedPropertyGroupAssignedConfigs, isFetchingAllPropertyGroupConfigsSuccess, UpdatingPropertyGroupConfigsError, isUpdatingPropertyGroupConfigsSuccess, isPropertyGroupAssignedConfigsRefreshRequired } = useSelector(selectPropertyGroupDetailsState);

  useEffect(() => {
    if (UpdatingPropertyGroupConfigsError.message?.length > 0) {
      setSnackBarOpen(true);
    }
  }, [UpdatingPropertyGroupConfigsError.message]);

  useEffect(() => {
    dispatch({
      type: selectSpecificPropertyGroupConfigs.type,
      payload: {
        propertyGroupUuid: propertyGroupIdParam,
      },
    });
  }, [isFetchingAllPropertyGroupConfigsSuccess]);

  useEffect(() => {
    resetPage();
  }, []);

  useEffect(() => {
    setPmConfig(selectedPropertyGroupAssignedConfigs);
    setErrorObj({});
  }, [selectedPropertyGroupAssignedConfigs]);

  const resetPage = () => {
    dispatch(clearPropertyGroupConfigSettingsFlags({}));
    dispatch({ type: getAvailableBankAccount.type, payload: { fundableOnly: true } });
    setIsDeleteModalOpen(false);
    setIsQuickPayModalOpen(false);
    setIsSpinnerModalOpen(false);
  };

  useEffect(() => {
    if (isUpdatingPropertyGroupConfigsSuccess) {
      dispatch(clearPropertyGroupConfigSettingsFlags({}));
      //Only fetch when there is an update on the quick pay
      if (isPropertyGroupAssignedConfigsRefreshRequired) dispatch(getAllPropertyGroupsConfigs());
      setIsDeleteModalOpen(false);
      setIsQuickPayModalOpen(false);
      setIsSpinnerModalOpen(false);
    }
  }, [isUpdatingPropertyGroupConfigsSuccess]);

  const [pendingConfig, setPendingConfig] = React.useState<any>(null);

  const handleChange = (ConfigTypeKey1: string, AssignedValue1: any, ConfigTypeKey2?: string, AssignedValue2?: any) => {
    let newPmConfig = {};

    if (ConfigTypeKey2 && AssignedValue2 !== undefined) {
      newPmConfig = {
        ...pmConfig,
        [ConfigTypeKey1]: AssignedValue1,
        [ConfigTypeKey2]: AssignedValue2,
      };
      setPendingConfig({
        ...pendingConfig,
        [ConfigTypeKey1]: AssignedValue1,
        [ConfigTypeKey2]: AssignedValue2,
      });
    } else {
      newPmConfig = {
        ...pmConfig,
        [ConfigTypeKey1]: AssignedValue1,
      };
      setPendingConfig({
        ...pendingConfig,
        [ConfigTypeKey1]: AssignedValue1,
      });
    }

    setPmConfig(newPmConfig);
  };

  const [openPartialPayPopup, setOpenPartialPayPopup] = React.useState(false);

  const handlePartialPayChange = (ConfigTypeKey1: string, AssignedValue1: any, ConfigTypeKey2?: string, AssignedValue2?: any) => {
    //If switch is turned off, show popup
    if (AssignedValue1 === defaultConfigValues.PartialPaymentsNotAfterDay + 1 && AssignedValue2 === defaultConfigValues.PartialPaymentsNotBeforeDay - 1) setOpenPartialPayPopup(true);
    //If switch is turned on, show popup
    if (pmConfig.PartialPaymentsNotBeforeDay === defaultConfigValues.PartialPaymentsNotAfterDay + 1 && pmConfig.PartialPaymentsNotAfterDay === defaultConfigValues.PartialPaymentsNotBeforeDay - 1) setOpenPartialPayPopup(true);

    handleChange(ConfigTypeKey1, AssignedValue1, ConfigTypeKey2, AssignedValue2);
  };

  useEffect(() => {
    if (openPartialPayPopup) {
      const timer = setTimeout(() => {
        setOpenPartialPayPopup(false);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [openPartialPayPopup]);

  const actuallyDispatchConfigUpdate = () => {
    if (pendingConfig) {
      dispatch(getUpdatePropertyGroupConfigs({ productTypeId: 1, updatedPmConfig: pendingConfig, propertyGroupUuid: propertyGroupIdParam }));
      setPendingConfig(null);
    }
  };

  const delayedConfigUpdate = useCallback(debounce(actuallyDispatchConfigUpdate, 750), [pendingConfig]);

  useEffect(() => {
    delayedConfigUpdate();
    return delayedConfigUpdate.cancel;
  }, [pendingConfig, delayedConfigUpdate]);

  const [errorObj, setErrorObj] = useState<ErrorObj>({});
  const handleError = (key: string, value: string) => setErrorObj({ ...errorObj, [key]: value });
  const confirmRestoreDefaults = () => {
    dispatch(getUpdatePropertyGroupConfigs({ productTypeId: 1, updatedPmConfig: {}, setToDefault: true, propertyGroupUuid: propertyGroupIdParam }));
  };

  const [openConfirmRestoreDefaults, setOpenConfirmRestoreDefaults] = useState(false);
  const confirmConfig = {
    classes: classes,
    openConfirmRestoreDefaults: openConfirmRestoreDefaults,
    setOpenConfirmRestoreDefaults: setOpenConfirmRestoreDefaults,
    confirmRestoreDefaults: confirmRestoreDefaults,
  };

  const getTypography = (text: string, fontWeight: number, fontSize: string) => (
    <Typography
      sx={{
        fontFamily: 'Work Sans',
        fontWeight: { fontWeight },
        fontSize: { fontSize },
        lineHeight: '22px',
      }}
    >
      {text}
    </Typography>
  );

  const QuickPayLink = selectedPropertyGroupAssignedConfigs?.Uuid ? `${window.location.origin}/quickpay/${selectedPropertyGroupAssignedConfigs.Uuid}` : '';

  const [selectedQPT, setSelectedQPT] = React.useState<QuickPayTypeDto>();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const handleConfirmDeleteModal = () => {
    setIsSpinnerModalOpen(true);
    selectedQPT &&
      dispatch(
        getUpdatePropertyGroupConfigs({
          productTypeId: 1,
          propertyGroupUuid: propertyGroupIdParam,
          updatedPmConfig: {
            QuickPayTypes: [
              {
                ...selectedQPT,
                IsDeleting: true,
              },
            ],
          },
        }),
      );
  };
  const [quickPayModalType, setQuickPayModalType] = React.useState<QuickPayModalType>(QuickPayModalType.Create);
  const [isQuickPayModalOpen, setIsQuickPayModalOpen] = React.useState(false);

  const [isSpinnerModalOpen, setIsSpinnerModalOpen] = React.useState(false);
  const [spinnerStatus, setSpinnerStatus] = React.useState<string | null>(null);
  const handleCloseSpinnerModal = React.useCallback(() => {
    setIsSpinnerModalOpen(false);
  }, [dispatch]);

  return (
    <Grid item xs={12} sx={{ mt: 3.5 }}>
      <Card sx={{ display: 'flex', flexDirection: 'column', color: '#000' }}>
        <Box className={classes.headerContainer}>
          <IconButton
            sx={{ marginRight: '10px', marginTop: '-4px' }}
            onClick={() => {
              resetPage();
              history.push(`/pmui/propertygroups/${propertyGroupIdParam}`);
            }}
          >
            <img src={whiteBackIcon} alt="" height="14px" />
          </IconButton>
          {pmConfig.PaymentsProductPurchased && pmConfig.PaymentsMasterFlag && (
            <FormControlLabel sx={{ color: '#FFF', ml: 0 }} control={<Switch checked={!!pmConfig.PaymentsProductEnabled} onChange={(event) => handleChange(event.target.name, event.target.checked)} name="PaymentsProductEnabled" />} label={getTypography(selectedPropertyGroupAssignedConfigs.Name + ': Payments', 600, '24px')} labelPlacement="start" />
          )}
        </Box>

        <Grid container sx={{ p: 2, pt: 2, pb: 1, position: 'relative' }}>
          <Grid item xs={12} md={6}>
            <Box className={classes.configContainer}>
              <Box className={classes.configHeaderContainer}>
                {getTypography('Payment Types', 600, '18px')}

                <Tooltip title="Enable or disabled the payment types you would like to accept." placement="right">
                  <HelpIcon sx={{ fontSize: 16, color: '#C4C4C4', cursor: 'pointer', ml: '10px' }} />
                </Tooltip>
              </Box>
              <Box className={classes.configBodyContainer}>
                <BeforeAfterSwitchConfig
                  name="AcceptsAch"
                  label="ACH payments"
                  description="Enable ACH for all or a portion of the month."
                  checked={!(pmConfig.AchNotAcceptedBeforeDay === defaultConfigValues.AchNotAcceptedAfterDay + 1 && pmConfig.AchNotAcceptedAfterDay === defaultConfigValues.AchNotAcceptedBeforeDay - 1)}
                  onChange={handleChange}
                  beforeValueName="AchNotAcceptedBeforeDay"
                  beforeValue={pmConfig.AchNotAcceptedBeforeDay}
                  defaultBeforeValue={defaultConfigValues.AchNotAcceptedBeforeDay}
                  afterValueName="AchNotAcceptedAfterDay"
                  afterValue={pmConfig.AchNotAcceptedAfterDay}
                  defaultAfterValue={defaultConfigValues.AchNotAcceptedAfterDay}
                  complex={true}
                  beforeError={errorObj.AchNotAcceptedBeforeDay}
                  afterError={errorObj.AchNotAcceptedAfterDay}
                  onError={handleError}
                />
                <BeforeAfterSwitchConfig
                  name="AcceptsDb"
                  label="Debit card payments"
                  description="Enable debit cards for all of the month."
                  checked={!(pmConfig.DbNotAcceptedBeforeDay === defaultConfigValues.DbNotAcceptedAfterDay + 1 && pmConfig.DbNotAcceptedAfterDay === defaultConfigValues.DbNotAcceptedBeforeDay - 1)}
                  onChange={handleChange}
                  beforeValueName="DbNotAcceptedBeforeDay"
                  beforeValue={pmConfig.DbNotAcceptedBeforeDay}
                  defaultBeforeValue={defaultConfigValues.DbNotAcceptedBeforeDay}
                  afterValueName="DbNotAcceptedAfterDay"
                  afterValue={pmConfig.DbNotAcceptedAfterDay}
                  defaultAfterValue={defaultConfigValues.DbNotAcceptedAfterDay}
                  complex={true}
                  beforeError={errorObj.DbNotAcceptedBeforeDay}
                  afterError={errorObj.DbNotAcceptedAfterDay}
                  onError={handleError}
                />
                <BeforeAfterSwitchConfig
                  name="AcceptsCc"
                  label="Credit card payments"
                  description="Enable credit cards for all of the month."
                  checked={!(pmConfig.CcNotAcceptedBeforeDay === defaultConfigValues.CcNotAcceptedAfterDay + 1 && pmConfig.CcNotAcceptedAfterDay === defaultConfigValues.CcNotAcceptedBeforeDay - 1)}
                  onChange={handleChange}
                  beforeValueName="CcNotAcceptedBeforeDay"
                  beforeValue={pmConfig.CcNotAcceptedBeforeDay}
                  defaultBeforeValue={defaultConfigValues.CcNotAcceptedBeforeDay}
                  afterValueName="CcNotAcceptedAfterDay"
                  afterValue={pmConfig.CcNotAcceptedAfterDay}
                  defaultAfterValue={defaultConfigValues.CcNotAcceptedAfterDay}
                  complex={true}
                  beforeError={errorObj.CcNotAcceptedBeforeDay}
                  afterError={errorObj.CcNotAcceptedAfterDay}
                  onError={handleError}
                />
              </Box>
            </Box>

            <Box className={classes.configContainer}>
              <Box className={classes.configHeaderContainer}>
                {getTypography('Recurring Payments', 600, '18px')}

                <Tooltip title="Configure customized rules for recurring payments." placement="right">
                  <HelpIcon sx={{ fontSize: 16, color: '#C4C4C4', cursor: 'pointer', ml: '10px' }} />
                </Tooltip>
              </Box>
              <Box className={classes.configBodyContainer}>
                <BeforeAfterSwitchConfig
                  name="VapAllowed"
                  label="Full balance only"
                  description="This recurring payment type ensures that your residents are always paid in full and on time. Be sure to restrict the allowed days based on when you post rent. It is best to leave 1 full day between when you regularly post and when the resident can schedule payments."
                  checked={!!pmConfig.VapAllowed}
                  onChange={handleChange}
                  beforeValueName="VapNotBeforeDate"
                  beforeValue={pmConfig.VapNotBeforeDate}
                  defaultBeforeValue={defaultConfigValues.VapNotBeforeDate}
                  afterValueName="VapNotAfterDate"
                  afterValue={pmConfig.VapNotAfterDate}
                  defaultAfterValue={defaultConfigValues.VapNotAfterDate}
                  beforeError={errorObj.VapNotBeforeDate}
                  afterError={errorObj.VapNotAfterDate}
                  onError={handleError}
                />
                <Box sx={{ mx: 4, mt: -1, display: 'flex', alignItems: 'center' }}>
                  <Tooltip title="Displays an option to allow the payer to set an upper limit on the amount they can be charged." placement="right">
                    <HelpIcon sx={{ fontSize: 16, color: '#C4C4C4', cursor: 'pointer', mr: '9px' }} />
                  </Tooltip>
                  <Typography
                    sx={{
                      fontFamily: 'Work Sans',
                      fontWeight: 400,
                      fontSize: '12px',
                      lineHeight: '22px',
                    }}
                  >
                    Allow Max Amount
                  </Typography>
                  <Checkbox checked={!!pmConfig.AutopayAllowPayerToSetMaxAmount} onChange={(e) => handleChange('AutopayAllowPayerToSetMaxAmount', !pmConfig.AutopayAllowPayerToSetMaxAmount)} inputProps={{ 'aria-label': 'controlled' }} disabled={!pmConfig.VapAllowed} />
                </Box>
                <BeforeAfterSwitchConfig
                  name="FapAllowed"
                  label="Specific amounts"
                  description="The recurring payment type ensures that your residents can pay a specific amount each month automatically. This should be enabled if you have co-habitants who both want their own recurring payments or do not have any miscellaneous charges that get posted mid-month. This can expose you to accepting partial payments."
                  checked={!!pmConfig.FapAllowed}
                  onChange={handleChange}
                  beforeValueName="FapNotBeforeDate"
                  beforeValue={pmConfig.FapNotBeforeDate}
                  defaultBeforeValue={defaultConfigValues.FapNotBeforeDate}
                  afterValueName="FapNotAfterDate"
                  afterValue={pmConfig.FapNotAfterDate}
                  defaultAfterValue={defaultConfigValues.FapNotAfterDate}
                  beforeError={errorObj.FapNotBeforeDate}
                  afterError={errorObj.FapNotAfterDate}
                  onError={handleError}
                />
                <SwitchConfig
                  name="CancelApOnLeaseEnd"
                  label="Cancel recurring payments on the move out date"
                  description="Automatically cancels a resident's recurring payment when they move out. It is important that you use the correct settings in your accounting software and have the appropriate business practices established so that this feature works well. It is ALWAYS the resident's responsibility to cancel their recurring payment if they move out."
                  checked={!!pmConfig.CancelApOnLeaseEnd}
                  onChange={handleChange}
                  showLabel={true}
                />
                <FrequencyConfig name="AutopayFrequenciesAllowed" label="Allowed frequencies:" description="Defines what cadence recurring payments can be set to execute on." value={pmConfig.AutopayFrequenciesAllowed ?? ''} onChange={handleChange} />
              </Box>
            </Box>

            <Box className={classes.configContainer}>
              <Box className={classes.configHeaderContainer}>
                {getTypography('Invitation Emails', 600, '18px')}

                <Tooltip title="Settings which control the sending of email communication to residents. This will not impact system emails like payment receipts and return notifications." placement="right">
                  <HelpIcon sx={{ fontSize: 16, color: '#C4C4C4', cursor: 'pointer', ml: '10px' }} />
                </Tooltip>
              </Box>
              <Box className={classes.configBodyContainer}>
                <SwitchConfig name="MarketingEnabled" label="Invitation emails" description="Allows invitation emails to be sent to residents manually or through automation." checked={!!pmConfig.MarketingEnabled} onChange={handleChange} />
                <DateConfig name="MarketingGoLiveDate" label={'Go live date:'} description="The date when all current residents are invited to use Inosio." onChange={handleChange} dateConfigValue={selectedPropertyGroupAssignedConfigs.MarketingGoLiveDate} />
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box className={classes.configContainer}>
              <Box className={classes.configHeaderContainer}>
                {getTypography('Payment Restrictions', 600, '18px')}

                <Tooltip title="Configure customized payment amount restrictions to meet your business needs." placement="right">
                  <HelpIcon sx={{ fontSize: 16, color: '#C4C4C4', cursor: 'pointer', ml: '10px' }} />
                </Tooltip>
              </Box>
              <Box className={classes.configBodyContainer}>
                <BeforeAfterSwitchConfig
                  name="ZeroPaymentsAllowed"
                  label="Pre-payments"
                  description="Allows residents to pay when they do not have a balance."
                  checked={!(pmConfig.ZeroPaymentsAllowedNotBefore === defaultConfigValues.ZeroPaymentsAllowedNotAfter + 1 && pmConfig.ZeroPaymentsAllowedNotAfter === defaultConfigValues.ZeroPaymentsAllowedNotBefore - 1)}
                  onChange={handleChange}
                  beforeValueName="ZeroPaymentsAllowedNotBefore"
                  beforeValue={pmConfig.ZeroPaymentsAllowedNotBefore}
                  defaultBeforeValue={defaultConfigValues.ZeroPaymentsAllowedNotBefore}
                  afterValueName="ZeroPaymentsAllowedNotAfter"
                  afterValue={pmConfig.ZeroPaymentsAllowedNotAfter}
                  defaultAfterValue={defaultConfigValues.ZeroPaymentsAllowedNotAfter}
                  complex={true}
                  beforeError={errorObj.ZeroPaymentsAllowedNotBefore}
                  afterError={errorObj.ZeroPaymentsAllowedNotAfter}
                  onError={handleError}
                />
                <BeforeAfterSwitchConfig
                  name="PartialPaymentsAllowed"
                  label="Partial-payments"
                  description="Allows residents to choose how much they can pay when they have a balance. This is good for roommates. It is common to restrict this after late fees have been applied."
                  checked={!(pmConfig.PartialPaymentsNotBeforeDay === defaultConfigValues.PartialPaymentsNotAfterDay + 1 && pmConfig.PartialPaymentsNotAfterDay === defaultConfigValues.PartialPaymentsNotBeforeDay - 1)}
                  onChange={handlePartialPayChange}
                  beforeValueName="PartialPaymentsNotBeforeDay"
                  beforeValue={pmConfig.PartialPaymentsNotBeforeDay}
                  defaultBeforeValue={defaultConfigValues.PartialPaymentsNotBeforeDay}
                  afterValueName="PartialPaymentsNotAfterDay"
                  afterValue={pmConfig.PartialPaymentsNotAfterDay}
                  defaultAfterValue={defaultConfigValues.PartialPaymentsNotAfterDay}
                  complex={true}
                  beforeError={errorObj.PartialPaymentsNotBeforeDay}
                  afterError={errorObj.PartialPaymentsNotAfterDay}
                  onError={handleError}
                />
                <BeforeAfterSwitchConfig
                  name="OverPaymentsAllowed"
                  label="Over-payments"
                  description="Allows residents to pay more than their current balance."
                  checked={!(pmConfig.OverPaymentsNotBefore === defaultConfigValues.OverPaymentsNotAfter + 1 && pmConfig.OverPaymentsNotAfter === defaultConfigValues.OverPaymentsNotBefore - 1)}
                  onChange={handleChange}
                  beforeValueName="OverPaymentsNotBefore"
                  beforeValue={pmConfig.OverPaymentsNotBefore}
                  defaultBeforeValue={defaultConfigValues.OverPaymentsNotBefore}
                  afterValueName="OverPaymentsNotAfter"
                  afterValue={pmConfig.OverPaymentsNotAfter}
                  defaultAfterValue={defaultConfigValues.OverPaymentsNotAfter}
                  complex={true}
                  beforeError={errorObj.OverPaymentsNotBefore}
                  afterError={errorObj.OverPaymentsNotAfter}
                  onError={handleError}
                />
                <ReadOnlyConfig label="Maximum payment amount accepted via Resident Portal" description="This is the maximum amount of money that can be transferred to you in a single payment. Contact support to request changes." value={pmConfig.MaxAllowedPayment ? `${convertCentsToDollars(pmConfig.MaxAllowedPayment)}` : ''} />
                <ReadOnlyConfig label="Maximum payment amount accepted via Payment Requests" description="This is the maximum amount of money that can be transferred to you in a single payment. Contact support to request changes." value={pmConfig.MaxAllowedPaymentRequestAmount ? `${convertCentsToDollars(pmConfig.MaxAllowedPaymentRequestAmount)}` : ''} />
                <InputConfig name="MaxNsfAllowed" label="Number of NSF’s before disabling ACH" description="This is the number of bounced online ACH payments that will be allowed before ACH is disabled for the resident." value={pmConfig.MaxNsfAllowed} onChange={handleChange} error={errorObj.MaxNsfAllowed} onError={handleError} />
              </Box>
            </Box>

            <Box className={classes.configContainer}>
              <Box className={classes.configHeaderContainer}>
                {getTypography('Quick Payments', 600, '18px')}

                <Tooltip title="QuickPay is an easy method of accepting payments from anyone. You should add the link to your website or you can email it to people that want to make a payment to you." placement="right">
                  <HelpIcon sx={{ fontSize: 16, color: '#C4C4C4', cursor: 'pointer', ml: '10px' }} />
                </Tooltip>
              </Box>
              <Box className={classes.configBodyContainer}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <SwitchConfig name="QuickPayEnabled" label="Quick Pay" description="Enable Quick Pay" checked={!!pmConfig.QuickPayEnabled} onChange={handleChange} showLabel={true} />
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    <Typography
                      style={{
                        fontFamily: 'Work Sans',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '22px',
                        color: '#000000',
                        marginRight: '40px',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {QuickPayLink}
                    </Typography>
                    <IconButton
                      edge="start"
                      color="inherit"
                      aria-label="Copy quick pay link"
                      onClick={() => {
                        navigator.clipboard.writeText(QuickPayLink);
                        setCopySnackBarOpen(true);
                      }}
                      sx={{
                        position: 'absolute',
                        right: 5,
                      }}
                    >
                      <ContentCopyIcon sx={{ width: '22px', height: '22px', fill: '#14142B' }} />
                    </IconButton>
                  </Box>
                </Box>

                <TableContainer sx={{ flex: 1, maxHeight: 265, margin: '0 -18px 10px', width: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell className={classes.headerCellText}>
                          <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                            Description
                          </Stack>
                        </TableCell>
                        <TableCell className={classes.headerCellText}>
                          <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                            Bank Account
                          </Stack>
                        </TableCell>
                        <TableCell className={classes.headerCellText}>
                          <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                            Minimum
                          </Stack>
                        </TableCell>
                        <TableCell className={classes.headerCellText}></TableCell>
                        <TableCell className={classes.headerCellText}>
                          <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                            <IconButton
                              onClick={() => {
                                setSelectedQPT(undefined);
                                setQuickPayModalType(QuickPayModalType.Create);
                                setIsQuickPayModalOpen(true);
                              }}
                              sx={{ p: 0.8, mr: '-8px' }}
                            >
                              <Icon sx={{ width: 26, height: 26 }}>
                                <img
                                  style={{
                                    cursor: 'pointer',
                                    width: 26,
                                    height: 26,
                                  }}
                                  src={AddIcon}
                                  alt="Add an item"
                                />
                              </Icon>
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedPropertyGroupAssignedConfigs?.QuickPayTypes?.map((qpt, index) => (
                        <TableRow key={index}>
                          <TableCell className={classes.bodyCellText}>
                            <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                              {qpt.Label ?? ''}
                            </Stack>
                          </TableCell>
                          <TableCell className={classes.bodyCellText}>
                            <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                              {qpt.Last4 ?? ''}
                            </Stack>
                          </TableCell>
                          <TableCell className={classes.bodyCellText}>
                            <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                              <NumberFormat thousandsGroupStyle="thousand" value={(qpt.MinimumPaymentAmount ?? 0) / 100} prefix="$" decimalSeparator="." displayType="text" type="text" thousandSeparator={true} allowNegative={true} decimalScale={2} fixedDecimalScale={true} />
                            </Stack>
                          </TableCell>
                          <TableCell className={classes.bodyCellText}>
                            <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                              <IconButton
                                onClick={() => {
                                  setSelectedQPT(qpt);
                                  setIsDeleteModalOpen(true);
                                }}
                                sx={{ p: 0.25 }}
                              >
                                <Icon sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <img
                                    style={{
                                      cursor: 'pointer',
                                      width: '15px',
                                      height: '18px',
                                    }}
                                    src={DeleteIcon}
                                    alt="Delete"
                                  />
                                </Icon>
                              </IconButton>
                            </Stack>
                          </TableCell>
                          <TableCell className={classes.bodyCellText}>
                            <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                              <IconButton
                                onClick={() => {
                                  setSelectedQPT(qpt);
                                  setQuickPayModalType(QuickPayModalType.Edit);
                                  setIsQuickPayModalOpen(true);
                                }}
                                sx={{ p: 0.25, mr: '-2px' }}
                              >
                                <Icon sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <img
                                    style={{
                                      cursor: 'pointer',
                                      width: '14px',
                                      height: '14px',
                                    }}
                                    src={EditIcon}
                                    alt="Edit"
                                  />
                                </Icon>
                              </IconButton>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>

            {isInosioUser && (
              <Box className={classes.configContainer}>
                <Box className={classes.configHeaderContainer}>
                  {getTypography('Owner Payments', 600, '18px')}

                  <Tooltip title="Owner payments configuration" placement="right">
                    <HelpIcon sx={{ fontSize: 16, color: '#C4C4C4', cursor: 'pointer', ml: '10px' }} />
                  </Tooltip>
                </Box>
                <Box className={classes.configBodyContainer}>
                  <SwitchConfig name="OwnerDistributionEnabled" label="Owner payments" description="Allows owner payments." checked={!!pmConfig.OwnerDistributionEnabled} onChange={handleChange} showLabel={true} />
                  <AmountInputConfig name="OwnerDistributionPerPmtLimit" label="Per Payment Amount" description="Owner payment amount per payment" value={pmConfig.OwnerDistributionPerPmtLimit} onChange={handleChange} error={errorObj.OwnerDistributionPerPmtLimit} onError={handleError} />
                  <AmountInputConfig name="OwnerDistributionThreeDayLimit" label="3 Day Amount" description="Owner payment amount for 3 days" value={pmConfig.OwnerDistributionThreeDayLimit} onChange={handleChange} error={errorObj.OwnerDistributionThreeDayLimit} onError={handleError} />
                  <AmountInputConfig name="OwnerDistributionOneMonthLimit" label="1 Month Amount" description="Owner payment amount for 1 month" value={pmConfig.OwnerDistributionOneMonthLimit} onChange={handleChange} error={errorObj.OwnerDistributionOneMonthLimit} onError={handleError} />
                </Box>
              </Box>
            )}
          </Grid>

          <Backdrop sx={{ color: '#fff', zIndex: 30, position: 'absolute', backgroundColor: 'rgb(255, 255, 255, 0.5)' }} open={!pmConfig.PaymentsProductEnabled || !pmConfig.PaymentsProductPurchased || !pmConfig.PaymentsMasterFlag}></Backdrop>
        </Grid>

        <Box id="dialogs">
          <ConfirmRestoreDefaultsDialog {...confirmConfig} />
        </Box>
      </Card>
      <Snackbar anchorOrigin={{ horizontal: 'center', vertical: 'top' }} open={snackBarOpen} autoHideDuration={4000} onClose={handleAlertClose}>
        <Alert onClose={handleAlertClose} severity="error">
          {UpdatingPropertyGroupConfigsError.message}
        </Alert>
      </Snackbar>
      <Snackbar anchorOrigin={{ horizontal: 'center', vertical: 'top' }} open={copySnackBarOpen} autoHideDuration={4000} onClose={handleAlertClose}>
        <Alert onClose={handleAlertClose} severity="success" sx={{ width: '100%' }}>
          Copied to Clipboard
        </Alert>
      </Snackbar>

      {isDeleteModalOpen && <DeleteModal open={isDeleteModalOpen} confirmCallback={handleConfirmDeleteModal} closeCallback={() => setIsDeleteModalOpen(false)} />}
      {isQuickPayModalOpen && <QuickPayModal open={isQuickPayModalOpen} mode={quickPayModalType} quickPayItem={selectedQPT} closeCallback={() => setIsQuickPayModalOpen(false)} showSpinnerModal={() => setIsSpinnerModalOpen(true)} />}
      {isSpinnerModalOpen && (
        <SpinnerModal
          width="472px"
          height="523px"
          isInvisible={true}
          openSpinnerModal={isSpinnerModalOpen}
          handleCloseSpinnerModal={handleCloseSpinnerModal}
          spinnerStatus={spinnerStatus}
          closeCancel={() => {
            setIsSpinnerModalOpen(false);
            setSpinnerStatus(null);
          }}
        />
      )}

      <Dialog
        onClose={() => {
          setOpenPartialPayPopup(false);
        }}
        open={openPartialPayPopup}
      >
        <DialogTitle>This will reset any lease overrides that may be in place.</DialogTitle>
      </Dialog>
    </Grid>
  );
}
